<template>
  <Toolbar v-if="isModal" class="mb-2" pt:root:class="mx-4 py-2">
    <template #start> </template>
    <template #center>
      <Select
        :options="codeTables"
        optionLabel="name"
        v-model="selectedCodeTable"
        class="w-72"
        scrollHeight="50rem"
        :disabled="defaultCodeTable != null"
      ></Select>
    </template>
  </Toolbar>
  <div class="flex flex-row w-full px-3">
    <Card v-if="!isModal" class="mx-1" pt:title:class="text-2xl font-bold text-center my-2" pt:content:class="p-2! w-96 h-full" pt:body:class="p-2!">
      <template #title>
        <h1>List Types</h1>
        <Divider></Divider>
      </template>
      <template #content>
        <div v-for="codeTable in codeTables">
          <Button class="w-full my-1 hover:font-bold" :outlined="codeTable !== selectedCodeTable" @click="selectedCodeTable = codeTable">{{
            codeTable.name
          }}</Button>
        </div>
      </template>
    </Card>
    <div v-if="selectedCodeTable != undefined" class="w-full">
      <ag-grid-vue
        theme="legacy"
        :rowData="selectedCodeTable.codes"
        :getRowId="(params: GetRowIdParams) => `${params.data.table}-${params.data.num}`"
        :columnDefs="colDefs"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitGridWidth' }"
        :domLayout="isModal ? 'autoHeight' : 'normal'"
        class="w-full"
        :class="getTableClass()"
        :singleClickEdit="true"
        :stopEditingWhenCellsLoseFocus="true"
        :pinnedBottomRowData="[{}]"
        :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
        :fullWidthCellRenderer="CustomAddNewCellRenderer"
        :fullWidthCellRendererParams="{
          callback: () => toggleMode('Add'),
          label: 'value',
          display: true,
        }"
        @cell-editing-stopped="updateCode"
        @row-data-updated="resize"
        @grid-size-changed="resize"
        @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
      >
      </ag-grid-vue>
    </div>
  </div>

  <TSDialog
    v-if="mode"
    size="w-[600px]!"
    :label="mode.toUpperCase() + ' ' + selectedCodeTable?.name"
    v-model:visible="displayModal"
    v-on:after-hide="toggleMode(undefined)"
    icon="plus-circle"
  >
    <template #content>
      <MaintainCode :mode="mode" :table="selectedCodeTable" @submitted="onDoneAdding()" />
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { type Code, type CodeTable, CodeTableListSchema } from "@/types/Codes";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import CustomAddNewCellRenderer from "@/components/cell-renderers/CustomAddNewCellRenderer.vue";
import { toFormData } from "@/services/Utility";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { useAuthStore } from "@/stores/auth";
import { getUserPermissions } from "@/services/Permissions";
import { CodeTableNumber } from "@/enums/CodeTable";
import type {
  CellEditingStoppedEvent,
  EditableCallbackParams,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  IsFullWidthRowParams,
  RowDataUpdatedEvent,
} from "ag-grid-community";

const mode = ref<"Add" | "Edit">();
const displayModal = ref(false);

const gridApi = ref<GridApi>();
const codeTables = ref<CodeTable[]>();
const selectedCodeTable = ref<CodeTable>();
const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));

const emit = defineEmits<{
  codesUpdated: [codes: Code[]];
}>();

interface Props {
  defaultCodeTable?: number;
  isModal?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isModal: false,
});

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

//TODO: Right now this allows deleting codes that are currently in use which should not be allowed

onMounted(async (): Promise<void> => {
  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  populateCodeTables(props.defaultCodeTable);
});

const populateCodeTables = (defaultTable: number | null = null): void => {
  codeTables.value = undefined;
  selectedCodeTable.value = undefined;

  axios
    .get("/codes")
    .then((response) => {
      codeTables.value = CodeTableListSchema.parse(response.data.result);
      if (!userPermissions.value.administrator && !userPermissions.value.managePropertyTypes) {
        codeTables.value = codeTables.value.filter((codeTable: CodeTable) => {
          return codeTable.tableNum !== CodeTableNumber.PROPERTY_TYPES && codeTable.tableNum !== CodeTableNumber.PROPERTY_TYPES_EX;
        });
      }
      if (!userPermissions.value.addLienWaiverDescriptions) {
        codeTables.value = codeTables.value.filter((codeTable: CodeTable) => {
          return codeTable.tableNum !== CodeTableNumber.WAIVER_DESCRIPTION;
        });
      }
      if (defaultTable != null) {
        codeTables.value.forEach((c) => {
          if (c.tableNum == defaultTable) selectedCodeTable.value = c;
        });
      } else {
        selectedCodeTable.value = codeTables.value[0];
      }
      if (selectedCodeTable.value) emit("codesUpdated", selectedCodeTable.value.codes);
    })
    .catch((e) => errorApi(e));
};

const refreshTable = (): void => {
  const currentTable = selectedCodeTable.value?.tableNum;
  populateCodeTables(currentTable);
};

const updateCode = (event: CellEditingStoppedEvent): void => {
  if (!event.valueChanged) return;

  const data: Code = event.data;
  axios
    .post("/codes/edit/" + selectedCodeTable.value?.tableNum + "/" + data.num, toFormData(data))
    .then((response) => {
      handleApiResponse(
        response,
        () => {
          const row = gridApi.value?.getRowNode(`${data.table}-${data.num}`);
          if (row == null) return;
          row.setDataValue("name", response.data.result.name);
          row.setDataValue("abbrev", response.data.result.abbrev);
          if (selectedCodeTable.value) emit("codesUpdated", selectedCodeTable.value.codes);
        },
        (errors: ApiError[]) => {
          error("Cannot update value", errors[0].message);
          const row = gridApi.value?.getRowNode(`${data.table}-${data.num}`);
          if (row == null) return;
          row.setDataValue(event.column.getColId(), event.oldValue);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const deleteCode = (data: Code): void => {
  axios
    .post("/codes/delete/" + selectedCodeTable.value?.tableNum + "/" + data.num)
    .then((response) => {
      handleApiResponse(
        response,
        () => {
          success("List value deleted.", "");
          refreshTable();
        },
        (errors: ApiError[]) => {
          error("Cannot delete value", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const getEditData = (name: string, abbrev: string): FormData => {
  const data = new FormData();
  data.append("name", name != null ? name : "");
  data.append("abbrev", abbrev != null ? abbrev : "");
  return data;
};

const onDoneAdding = (): void => {
  toggleMode(undefined);
  refreshTable();
};

const toggleMode = (value: "Add" | "Edit" | undefined): void => {
  mode.value = value;
  displayModal.value = value != null;
};

const getTableClass = (): string => {
  let tableClass = "";
  tableClass += props.isModal ? " " : "h-[80vh] ";
  tableClass += darkVal.value ? "ag-theme-quartz-dark" : "ag-theme-quartz";
  return tableClass;
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    width: 0,
    editable: false,
    cellClass: "text-center flex items-center justify-center",
    headerClass: "text-center",
    filter: false,
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: () => "Are you sure you want to delete this value from the list?",
      deleteConfirmHeader: () => "Delete List Value",
      callback: deleteCode,
      checkDeletable: (data: Code) => !data.systemRequired,
    },
  },
  {
    field: "name",
    headerName: "Name",
    width: 100,
    cellEditorParams: { maxLength: 250 },
    editable: (params: EditableCallbackParams) => !params.data.systemRequired,
  },
  {
    field: "abbrev",
    headerName: "Abbreviation (Optional)",
    width: 100,
    cellEditorParams: { maxLength: 12 },
    editable: (params: EditableCallbackParams) => !params.data.systemRequired,
  },
]);
</script>
